import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { CandidateImportDuplicateCheckList } from './CandidateImportDuplicateCheckList';
import { DuplicateConfirmDialog } from './DuplicateConfirmDialog';
import type { AgentApiCandidateImportRequestsShow } from '@/types/serializers';
import { useBulkCreateCandidates } from '../api/useBulkCreateCandidates';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';

interface Props {
  importRequest: AgentApiCandidateImportRequestsShow;
  encryptId: string;
}

export function CandidateImportConfirmView({ importRequest, encryptId }: Props) {
  const navigate = useNavigate();
  const bulkCreateMutation = useBulkCreateCandidates();
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>([]);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  // 初期選択を設定（全選択）
  useEffect(() => {
    if (importRequest.tmp_candidate_profiles) {
      setSelectedCandidateIds(importRequest.tmp_candidate_profiles.map((c) => c.id));
    }
  }, [importRequest.tmp_candidate_profiles]);

  // 選択された候補者の中で重複があるかチェック
  const getDuplicateCandidates = () => {
    if (!importRequest.tmp_candidate_profiles) return [];

    return importRequest.tmp_candidate_profiles.filter(
      (candidate) =>
        selectedCandidateIds.includes(candidate.id) &&
        (candidate.is_duplicate_csv || candidate.is_duplicate_record)
    );
  };

  const handleBulkCreateClick = () => {
    const duplicates = getDuplicateCandidates();

    // 重複がある場合は確認ダイアログを表示
    if (duplicates.length > 0) {
      setShowDuplicateDialog(true);
    } else {
      // 重複がない場合は直接実行
      handleBulkCreate();
    }
  };

  const handleBulkCreate = async () => {
    try {
      await bulkCreateMutation.mutateAsync({
        encryptId,
        selectedIds: selectedCandidateIds,
      });

      showSuccessToast(`取り込みを ${selectedCandidateIds.length} 件完了しました`);
      // 取り込み完了後、新規画面に戻る
      navigate({ to: '/agent/candidates/candidate_import_requests/new' });
    } catch (error) {
      showErrorToast('取り込みに失敗しました');
    }
  };

  const handleDialogConfirm = () => {
    setShowDuplicateDialog(false);
    handleBulkCreate();
  };

  const handleDialogCancel = () => {
    setShowDuplicateDialog(false);
  };

  const duplicateNames = getDuplicateCandidates().map((c) => c.name || '名前なし');

  return (
    <div className="flex flex-col gap-0">
      <div className="rounded-sm bg-white p-lg shadow-layer1">
        <div className="space-y-lg">
          {/* ヘッダー */}
          <div className="border-b border-border pb-lg">
            <h2 className="text-heading-md">取り込みデータの確認</h2>
          </div>

          {/* 説明 */}
          <div>
            <p className="text-body-md">取り込む求職者データを選択してください。</p>
          </div>

          {/* 重複チェックリスト */}
          <CandidateImportDuplicateCheckList
            candidates={importRequest.tmp_candidate_profiles || []}
            selectedIds={selectedCandidateIds}
            onSelectionChange={setSelectedCandidateIds}
          />
        </div>
      </div>

      {/* 固定フッター */}
      <div className="fixed right-0 bottom-0 left-[250px] z-10 flex items-center justify-end gap-2 bg-white px-lg py-md shadow-layer3">
        <Button
          color="primary"
          size="lg"
          type="button"
          onClick={handleBulkCreateClick}
          disabled={bulkCreateMutation.isPending || selectedCandidateIds.length === 0}
        >
          {bulkCreateMutation.isPending ? '処理中...' : '取り込みを完了'}
        </Button>
      </div>

      {/* 重複確認ダイアログ */}
      <DuplicateConfirmDialog
        open={showDuplicateDialog}
        duplicateNames={duplicateNames}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      />
    </div>
  );
}
